/**
 * Scrolling a row to somewhere it can actually be seen.
 *
 * "Inside the viewport" is not the same as "visible" on this screen. The add bar is
 * sticky along the bottom, and on iOS the keyboard covers the layout viewport rather than
 * shrinking it, so the foot of the screen is furniture: anything below the add bar's top
 * edge is behind something. Every measurement here uses that edge as the floor rather
 * than `window.innerHeight`, which is why this is a module and not a one-line call.
 */

/** How long to keep watching for the keyboard after the add box takes focus. */
const KeyboardSettleMs = 800;

/** Breathing space between the row and the bar, so it does not read as tucked under it. */
const RestingGapPx = 8;

interface RevealOptions {
  /** Scroll even if the row is already visible — for pointing at something. */
  force?: boolean;
}

export function revealItem(itemId: string, options: RevealOptions = {}): void {
  const row = document.getElementById(`item-${itemId}`);

  if (!row || (options.force !== true && isVisible(row))) {
    return;
  }

  row.scrollIntoView({ block: 'center', behavior: scrollBehaviour() });
}

/**
 * Bringing the end of the list up as the add box takes focus.
 *
 * Re-running while the viewport settles is what makes tapping the add box bring the end
 * of the list up with it — see `whileKeyboardSettles`.
 *
 * This parks the row rather than calling `revealItem`, and the difference is the whole
 * bug it fixes: `revealItem` leaves an already-visible row alone, so the first tap moved
 * the list, and every tap after that found the row still on screen from last time and
 * decided there was nothing to do. Where the list ends up must not depend on where the
 * previous tap left it.
 */
export function revealItemAsKeyboardOpens(itemId: string): void {
  whileKeyboardSettles(() => parkAboveAddBar(itemId));
}

/**
 * Bringing the whole of the open item editor into view.
 *
 * The editor replaces its row in place, and it is several times taller: two fields, the
 * assignment picker and three buttons where there was one line. It also focuses the name
 * field, so the keyboard arrives at the same moment — and a row that was comfortably
 * above the add bar becomes an editor whose Save and Remove buttons are behind it. The
 * browser's own scroll-the-focused-input-into-view is no help, because it is satisfied
 * the moment the *name* field is visible, which is the one part that never needed
 * scrolling to.
 *
 * This fits rather than parks, unlike the add box above: an editor already fully in view
 * is left where it is, because the surrounding rows are what tell you where in the list
 * you are, and dragging the list every time the pen is tapped throws that away for
 * nothing.
 */
export function revealEditorAsKeyboardOpens(itemId: string): void {
  whileKeyboardSettles(() => fitAboveAddBar(itemId));
}

/**
 * Runs a scroll now, and again as the keyboard changes the shape of the screen.
 *
 * iOS reports the smaller visual viewport a few hundred milliseconds *after* focus, so a
 * single scroll now measures a screen that is about to change shape — and the keyboard
 * then covers whatever we just scrolled to. Every resize until it settles, not just the
 * first: the keyboard animates in, and the first report is usually of a screen half way
 * there.
 *
 * Whatever is passed must therefore be safe to run repeatedly — see the note on each.
 */
function whileKeyboardSettles(scroll: () => void): void {
  scroll();

  const viewport = window.visualViewport;
  if (!viewport) {
    return;
  }

  viewport.addEventListener('resize', scroll);
  window.setTimeout(() => viewport.removeEventListener('resize', scroll), KeyboardSettleMs);
}

/**
 * Scrolls so the row comes to rest just above the add bar, wherever it is now.
 *
 * Idempotent by construction: it measures the distance to a fixed resting place and
 * scrolls exactly that far, so running it again once the row is already there computes
 * zero and does nothing — rather than being *told* there is nothing to do by a visibility
 * test the previous run is what satisfied.
 *
 * A pending smooth scroll is not a problem: `scrollBy` is relative to where the page has
 * actually reached, and the rect is read at the same instant, so a re-run mid-animation
 * retargets rather than overshooting.
 */
function parkAboveAddBar(itemId: string): void {
  const row = document.getElementById(`item-${itemId}`);

  if (!row) {
    return;
  }

  const delta = row.getBoundingClientRect().bottom + RestingGapPx - floor();

  // A fractional device pixel ratio leaves permanent sub-pixel noise here, and noise is
  // not a scroll.
  if (Math.abs(delta) < 1) {
    return;
  }

  window.scrollBy({ top: delta, behavior: scrollBehaviour() });
}

/**
 * Scrolls the least distance that puts the whole of a row — or the editor standing in for
 * it — between the top of the screen and the add bar.
 *
 * Idempotent for the same reason `parkAboveAddBar` is: every branch scrolls to a resting
 * place computed from the edges of the readable band rather than from where the element
 * happens to be, so a second run over a settled layout computes zero. It has to be, since
 * it runs on every viewport resize while the keyboard opens.
 *
 * Something taller than the band is aligned to its top instead. A form is read downwards,
 * and an editor that cannot fit is far better shown from the name field with the buttons
 * one flick away than from the buttons with the name off the top of the screen.
 */
function fitAboveAddBar(itemId: string): void {
  const element = document.getElementById(`item-${itemId}`);

  if (!element) {
    return;
  }

  const rect = element.getBoundingClientRect();
  const resting = floor() - RestingGapPx;
  const isTallerThanTheBand = rect.bottom - rect.top > resting - RestingGapPx;

  // Off the top is measured against the edge itself, not the resting gap: a few pixels of
  // an element tucked under the top is not worth moving the list for, and treating it as
  // if it were would make this scroll on a page that has not been laid out yet.
  const delta =
    isTallerThanTheBand || rect.top < 0
      ? rect.top - RestingGapPx
      : Math.max(rect.bottom - resting, 0);

  // A fractional device pixel ratio leaves permanent sub-pixel noise here, and noise is
  // not a scroll.
  if (Math.abs(delta) < 1) {
    return;
  }

  window.scrollBy({ top: delta, behavior: scrollBehaviour() });
}

function isVisible(row: HTMLElement): boolean {
  const rect = row.getBoundingClientRect();

  return rect.top >= 0 && rect.bottom <= floor();
}

/** The top of the add bar, below which nothing can be read. */
function floor(): number {
  const addBar = document.querySelector('.add-bar');

  return addBar ? addBar.getBoundingClientRect().top : window.innerHeight;
}

function scrollBehaviour(): ScrollBehavior {
  // The CSS media query covers animations and transitions; a smooth scroll is neither.
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}
